import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Markdown } from '@/components/ui/markdown';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { CANDIDATE_STATUS_LABEL } from '../constants';
import { useCandidate, useDeleteCandidate, useOpening } from '../queries';
import { candidateName } from '../types';

export function CandidateDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: candidate, isPending, isError, error } = useCandidate(id);
  const opening = useOpening(candidate?.job_opening ?? '');
  const remove = useDeleteCandidate();

  if (isPending) {
    return (
      <div className="max-w-3xl space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/hrm/recruitment/candidates')}>
          <ArrowLeft /> Candidates
        </Button>
        <p className="text-destructive">{(error as unknown as ApiError).message}</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/hrm/recruitment/candidates')}
        >
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">{candidateName(candidate)}</h1>
        <Badge variant="secondary">{CANDIDATE_STATUS_LABEL[candidate.status]}</Badge>
        <div className="ml-auto flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/hrm/recruitment/candidates/${candidate.public_id}/edit`)}
          >
            <Pencil /> Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash2 /> Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete “{candidateName(candidate)}”?</AlertDialogTitle>
                <AlertDialogDescription>
                  This candidate will be removed from the pipeline. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={remove.isPending}
                  onClick={() =>
                    remove.mutate(candidate.id, {
                      onSuccess: () => navigate('/hrm/recruitment/candidates'),
                    })
                  }
                >
                  {remove.isPending ? 'Deleting…' : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground grid gap-4 text-sm sm:grid-cols-3">
          <Field label="Email">{candidate.email || '—'}</Field>
          <Field label="Phone">{candidate.phone_number || '—'}</Field>
          <Field label="Source">{candidate.source || '—'}</Field>
          <Field label="Telegram">{candidate.telegram || '—'}</Field>
          <Field label="WhatsApp">{candidate.whatsapp || '—'}</Field>
          <Field label="Applied for">
            {candidate.job_opening ? (
              <Button
                variant="link"
                className="h-auto p-0"
                disabled={!opening.data}
                onClick={() =>
                  opening.data && navigate(`/hrm/recruitment/${opening.data.public_id}`)
                }
              >
                {opening.data?.title ?? `Opening #${candidate.job_opening}`}
              </Button>
            ) : (
              '—'
            )}
          </Field>
          <Field label="Created">{new Date(candidate.created).toLocaleDateString()}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          {candidate.notes ? (
            <Markdown>{candidate.notes}</Markdown>
          ) : (
            <p className="text-muted-foreground text-sm">No notes.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase">{label}</p>
      <div className="text-foreground mt-1">{children}</div>
    </div>
  );
}
